/**
 * BOB ASSISTANT API Service
 * Servicio para interactuar con los endpoints del dashboard BOB ASSISTANT
 */

import axiosInstance from '../AxiosInstance';

const BOB_ASSISTANT_BASE = 'bob-assistant';

/**
 * Obtiene el dashboard completo del dispositivo
 * @param {number} deviceId - ID del dispositivo
 * @returns {Promise} - Datos del dashboard
 */
export const getDashboard = async (deviceId) => {
    const response = await axiosInstance.get(`${BOB_ASSISTANT_BASE}/${deviceId}/dashboard/`);
    return response.data;
};

/**
 * Obtiene el historial de reportes
 * @param {number} deviceId - ID del dispositivo
 * @param {Object} params - Parámetros de filtro
 * @param {number} params.days - Número de días a consultar (default: 30)
 * @param {number} params.limit - Límite de registros (default: 100)
 * @returns {Promise} - Lista de reportes
 */
export const getReports = async (deviceId, params = {}) => {
    const response = await axiosInstance.get(`${BOB_ASSISTANT_BASE}/${deviceId}/reports/`, { params });
    return response.data;
};

/**
 * Obtiene el historial de alarmas
 * @param {number} deviceId - ID del dispositivo
 * @param {Object} params - Parámetros de filtro
 * @param {boolean} params.acknowledged - Filtrar por estado de reconocimiento
 * @param {number} params.days - Número de días a consultar (default: 90)
 * @param {number} params.limit - Límite de registros (default: 50)
 * @returns {Promise} - Lista de alarmas
 */
export const getAlarms = async (deviceId, params = {}) => {
    const response = await axiosInstance.get(`${BOB_ASSISTANT_BASE}/${deviceId}/alarms/`, { params });
    return response.data;
};

/**
 * Reconoce una alarma
 * @param {number} deviceId - ID del dispositivo
 * @param {number} alarmId - ID de la alarma
 * @param {string} notes - Notas opcionales
 * @returns {Promise} - Resultado de la operación
 */
export const acknowledgeAlarm = async (deviceId, alarmId, notes = '') => {
    const response = await axiosInstance.post(
        `${BOB_ASSISTANT_BASE}/${deviceId}/alarms/${alarmId}/acknowledge/`,
        { notes }
    );
    return response.data;
};

/**
 * Obtiene datos del heatmap de tiempo de operación
 * @param {number} deviceId - ID del dispositivo
 * @returns {Promise} - Datos del heatmap (matriz 7x24)
 */
export const getHeatmap = async (deviceId) => {
    const response = await axiosInstance.get(`${BOB_ASSISTANT_BASE}/${deviceId}/heatmap/`);
    return response.data;
};

/**
 * Obtiene datos de análisis FFT
 * @param {number} deviceId - ID del dispositivo
 * @param {number} alarmId - ID de alarma específica (opcional)
 * @returns {Promise} - Datos FFT
 */
export const getFFTAnalysis = async (deviceId, alarmId = null) => {
    const params = alarmId ? { alarm_id: alarmId } : {};
    const response = await axiosInstance.get(`${BOB_ASSISTANT_BASE}/${deviceId}/fft_analysis/`, { params });
    return response.data;
};

/**
 * Obtiene historial de vibración
 * @param {number} deviceId - ID del dispositivo
 * @param {number} days - Número de días (default: 7)
 * @returns {Promise} - Datos de vibración
 */
export const getVibrationHistory = async (deviceId, days = 7) => {
    const response = await axiosInstance.get(`${BOB_ASSISTANT_BASE}/${deviceId}/vibration_history/`, {
        params: { days }
    });
    return response.data;
};

/**
 * Obtiene historial de temperatura
 * @param {number} deviceId - ID del dispositivo
 * @param {number} days - Número de días (default: 7)
 * @returns {Promise} - Datos de temperatura
 */
export const getTemperatureHistory = async (deviceId, days = 7) => {
    const response = await axiosInstance.get(`${BOB_ASSISTANT_BASE}/${deviceId}/temperature_history/`, {
        params: { days }
    });
    return response.data;
};

/**
 * Obtiene distribución de anomalías
 * @param {number} deviceId - ID del dispositivo
 * @param {number} days - Número de días (default: 30)
 * @returns {Promise} - Distribución de anomalías
 */
export const getAnomalyDistribution = async (deviceId, days = 30) => {
    const response = await axiosInstance.get(`${BOB_ASSISTANT_BASE}/${deviceId}/anomaly_distribution/`, {
        params: { days }
    });
    return response.data;
};

/**
 * Obtiene eventos de estado (start/stop)
 * @param {number} deviceId - ID del dispositivo
 * @param {Object} params - Parámetros de filtro
 * @returns {Promise} - Lista de eventos
 */
export const getStateEvents = async (deviceId, params = {}) => {
    const response = await axiosInstance.get(`${BOB_ASSISTANT_BASE}/${deviceId}/state_events/`, { params });
    return response.data;
};

/**
 * Obtiene historial de aprendizaje
 * @param {number} deviceId - ID del dispositivo
 * @returns {Promise} - Datos de aprendizaje
 */
export const getLearningHistory = async (deviceId) => {
    const response = await axiosInstance.get(`${BOB_ASSISTANT_BASE}/${deviceId}/learning_history/`);
    return response.data;
};

/**
 * Obtiene lista de sistemas de mantenimiento predictivo
 * @returns {Promise} - Lista de sistemas
 */
export const getPredictiveMaintenanceSystems = async () => {
    const response = await axiosInstance.get('predictive-maintenance-systems/');
    return response.data;
};

export default {
    getDashboard,
    getReports,
    getAlarms,
    acknowledgeAlarm,
    getHeatmap,
    getFFTAnalysis,
    getVibrationHistory,
    getTemperatureHistory,
    getAnomalyDistribution,
    getStateEvents,
    getLearningHistory,
    getPredictiveMaintenanceSystems,
};
